import React, { useEffect, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../../../styles/dashboardTheme.css";
import "../../../../styles/managementTheme.css";
import "../../empleados/styles/rrhh.css";
import { usePermissions } from "../../../../utils/permissions";

function Modal({ open, title, onClose, children }) {
  if (!open) return null;
  return (
    <div className="modal fade show d-block management-modal" tabIndex="-1"
      style={{ backgroundColor: "rgba(15,23,42,0.45)" }} aria-modal="true" role="dialog">
      <div className="modal-dialog modal-md modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header border-0">
            <h5 className="modal-title">{title}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

function PuestoForm({ form, setForm, onSubmit, onClose, saving }) {
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
      <div className="modal-body pt-0">
        <div className="row g-3">
          <div className="col-12">
            <label className="form-label management-label">Nombre *</label>
            <input
              className="form-control"
              value={form.nombre}
              onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))}
              required
              autoFocus
            />
          </div>
          <div className="col-12">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="puesto-activo"
                checked={form.activo}
                onChange={(e) => setForm((p) => ({ ...p, activo: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="puesto-activo">Activo</label>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-footer border-0 pt-0">
        <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancelar</button>
        <button type="submit" className="btn management-btn-primary" disabled={saving}>
          {saving ? "Guardando..." : "Guardar"}
        </button>
      </div>
    </form>
  );
}

const formatFecha = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-EC", { year: "numeric", month: "2-digit", day: "2-digit" });
};

export default function PuestosView({
  puestos, loading, error, reload,
  modal, saving, editando, deleting,
  formNuevo, setFormNuevo,
  formEditar, setFormEditar,
  abrirCrear, abrirEditar, abrirEliminar, cerrarModal,
  handleCrear, handleActualizar, handleEliminar,
}) {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission("rrhh.empleados.crear");
  const canUpdate = hasPermission("rrhh.empleados.editar");

  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const wrapRef = useRef(null);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (modal) document.body.classList.add("modal-open");
    else document.body.classList.remove("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, [modal]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  return (
    <section className="management-page management-theme-blue rrhh-wrapper">
      <div className="management-card management-header mb-4">
        <div>
          <p className="management-eyebrow mb-1">RRHH / Puestos</p>
          <h4 className="management-title mb-1 d-flex align-items-center gap-2">
            <i className="bi bi-briefcase" />
            Catálogo de puestos
          </h4>
          <p className="text-muted mb-0">Define los puestos de trabajo del centro.</p>
        </div>
        {canCreate && (
          <button type="button" className="btn management-btn-primary" onClick={abrirCrear}>
            <i className="bi bi-plus-circle me-2" />
            Nuevo puesto
          </button>
        )}
      </div>

      <div className="management-card rrhh-table-card">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="mb-0">Puestos registrados</h6>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={reload} disabled={loading}>
            <i className="bi bi-arrow-repeat me-2" />Actualizar
          </button>
        </div>

        {loading && <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>}

        {error && (
          <div className="management-alert d-flex justify-content-between align-items-center">
            <span>{error}</span>
            <button className="btn btn-outline-secondary btn-sm" onClick={reload}>Reintentar</button>
          </div>
        )}

        {!loading && !error && !puestos.length && (
          <div className="dashboard-empty text-center py-5">
            <i className="bi bi-briefcase dashboard-empty-icon mb-3" />
            <h5 className="fw-semibold mb-1">No hay puestos registrados.</h5>
            <p className="text-muted mb-0">Crea el primer puesto de trabajo.</p>
          </div>
        )}

        {!loading && !error && puestos.length > 0 && (
          <div className="management-table-wrapper">
            <table className="table align-middle management-table rrhh-table mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Nombre</th>
                  <th className="text-center">Estado</th>
                  <th>Creado</th>
                  <th>Modificado</th>
                  {canUpdate && <th className="text-end">Acciones</th>}
                </tr>
              </thead>
              <tbody ref={wrapRef}>
                {puestos.map((p, idx) => {
                  const isMenuOpen = openMenuId === p.id;
                  return (
                    <tr key={p.id}>
                      <td className="fw-semibold">{idx + 1}</td>
                      <td className="fw-semibold">{p.nombre}</td>
                      <td className="text-center">
                        <span className={`badge rounded-pill px-3 py-2 ${p.activo ? "bg-success-subtle text-success" : "bg-secondary-subtle text-muted"}`}>
                          {p.activo ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="text-muted">{formatFecha(p.created_at)}</td>
                      <td className="text-muted">{formatFecha(p.updated_at)}</td>
                      {canUpdate && (
                        <td className="text-end">
                          <div className="management-actions-wrap">
                            <button
                              type="button"
                              className={`management-actions-toggle${isMenuOpen ? " is-open" : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isMenuOpen) {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                                }
                                setOpenMenuId(isMenuOpen ? null : p.id);
                              }}
                              title="Acciones"
                              aria-expanded={isMenuOpen}
                            >
                              <i className="bi bi-three-dots-vertical" />
                            </button>
                            {isMenuOpen && (
                              <ul className="management-actions-menu" style={{ position: "fixed", top: menuPos.top, right: menuPos.right, left: "auto" }}>
                                <li>
                                  <button
                                    type="button"
                                    className="menu-item"
                                    onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); abrirEditar(p); }}
                                  >
                                    <i className="bi bi-pencil-square" />
                                    Editar
                                  </button>
                                </li>
                                <li>
                                  <button
                                    type="button"
                                    className="menu-item is-danger"
                                    onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); abrirEliminar(p); }}
                                  >
                                    <i className="bi bi-trash" />
                                    Eliminar
                                  </button>
                                </li>
                              </ul>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={modal === "crear"} title="Nuevo puesto" onClose={cerrarModal}>
        <PuestoForm form={formNuevo} setForm={setFormNuevo} onSubmit={handleCrear} onClose={cerrarModal} saving={saving} />
      </Modal>
      <Modal open={modal === "editar"} title="Editar puesto" onClose={cerrarModal}>
        <PuestoForm form={formEditar} setForm={setFormEditar} onSubmit={handleActualizar} onClose={cerrarModal} saving={saving} />
      </Modal>

      <Modal open={modal === "eliminar"} title="Eliminar puesto" onClose={cerrarModal}>
        <div className="modal-body pt-0">
          <p className="mb-2">
            Estás a punto de eliminar el puesto <strong>{editando?.nombre || "seleccionado"}</strong>.
          </p>
          <p className="text-muted mb-0">
            Esta acción no se puede deshacer. Si el puesto tiene empleados asociados (histórico incluido), no se podrá eliminar y deberás desactivarlo en su lugar.
          </p>
        </div>
        <div className="modal-footer border-0 pt-0">
          <button type="button" className="btn btn-outline-secondary" onClick={cerrarModal} disabled={deleting}>
            Cancelar
          </button>
          <button type="button" className="btn btn-danger" onClick={handleEliminar} disabled={deleting}>
            {deleting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                Eliminando...
              </>
            ) : (
              "Eliminar"
            )}
          </button>
        </div>
      </Modal>
    </section>
  );
}
